/**
 * audioGraph.js — single source of truth for Web Audio routing.
 *
 * The browser allows ONE MediaElementAudioSourceNode per <audio> element.
 * Previously each visualizer (OrbWaveform, SpectrumBars, TalkingHead,
 * MainRobotHead, AgentFaceStage) tried to call createMediaElementSource()
 * independently → "MediaElementSource already connected" InvalidStateError
 * → agent TTS audio silently dropped → Tom/Sarah didn't speak.
 *
 * All components now go through getOrCreateSource(audioEl) which caches
 * the source node on the element itself + on a window registry so it
 * survives module HMR. A single connection to ctx.destination is made
 * once per element so audio reaches the speakers.
 */

let _ctx = null;

export function getAudioContext() {
  if (_ctx) return _ctx;
  if (typeof window === "undefined") return null;
  if (window.__rcAudioCtx) { _ctx = window.__rcAudioCtx; return _ctx; }
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  _ctx = new AC();
  window.__rcAudioCtx = _ctx;
  return _ctx;
}

/**
 * Get (or lazily create) the MediaElementSourceNode for an <audio> element.
 * Returns null if the browser refuses (cross-origin without CORS, missing
 * AudioContext support, or first creation throws). The element is marked
 * with `__rcMediaSourceFailed` after a failure so we don't retry forever.
 */
export function getOrCreateSource(audioEl) {
  if (!audioEl) return null;
  if (audioEl.__rcMediaSourceFailed) return null;
  if (audioEl.__rcMediaSource) return audioEl.__rcMediaSource;
  const ctx = getAudioContext();
  if (!ctx) return null;
  try {
    const src = ctx.createMediaElementSource(audioEl);
    audioEl.__rcMediaSource = src;
    // Route to speakers ONCE per element. Analysers are tapped off this
    // node by individual visualizers; the source→destination edge is
    // shared so we never double-route audio.
    try { src.connect(ctx.destination); } catch { /* ignore */ }
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    return src;
  } catch (e) {
    // Most common cause: this exact element ALREADY has a source somewhere
    // (HMR survivor, or another path beat us). Mark + return null so the
    // caller falls back to a sine-wobble mouth.
    audioEl.__rcMediaSourceFailed = true;
    console.warn("[audioGraph] createMediaElementSource failed:", e?.message);
    return null;
  }
}

/**
 * Attach a fresh AnalyserNode to a shared source. Each visualizer gets
 * its OWN analyser (so they can have different fftSize / smoothing
 * params) but they all read from the same upstream source node.
 * Returns null if the source is unavailable.
 */
export function attachAnalyser(audioEl, { fftSize = 256, smoothing = 0.8 } = {}) {
  const src = getOrCreateSource(audioEl);
  if (!src) return null;
  const ctx = getAudioContext();
  if (!ctx) return null;
  const analyser = ctx.createAnalyser();
  analyser.fftSize = fftSize;
  analyser.smoothingTimeConstant = smoothing;
  try { src.connect(analyser); } catch { /* ignore */ }
  return analyser;
}
